import React, { useEffect, useState } from 'react';
import { ScaleLoader } from 'react-spinners';
import { getemployeeTable, updateDispatchStatus, UpdateEmployeeProcessStatus } from '../../../api/employeeApi';
import { useAuth } from '../../../context/AuthProvider';
import toast from 'react-hot-toast';
import ReceiptUploadModal from '../../track/ReceiptUploadModal';
import QualityCheckModal from '../../QualityCheckModal/QualityCheckModal';
import { MdClose, MdDone } from 'react-icons/md';
import { FaCheckCircle, FaHourglassHalf, FaHourglassStart } from 'react-icons/fa';
import {
  XMarkIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

/* ── Status config ── */
const STATUS_MAP = {
  upcoming: { label: 'Upcoming', pill: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200', dot: 'bg-indigo-500' },
  'up coming': { label: 'Upcoming', pill: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200', dot: 'bg-indigo-500' },
  'yet not start': { label: 'Yet Not Start', pill: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200', dot: 'bg-blue-400' },
  inprogress: { label: 'In Progress', pill: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200', dot: 'bg-amber-500' },
  'in progress': { label: 'In Progress', pill: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200', dot: 'bg-amber-500' },
  completed: { label: 'Completed', pill: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200', dot: 'bg-emerald-500' },
  rejected: { label: 'Rejected', pill: 'bg-red-50 text-red-700 ring-1 ring-red-200', dot: 'bg-red-500' },
  'partical completed': { label: 'Partial', pill: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200', dot: 'bg-orange-400' },
  default: { label: 'Unknown', pill: 'bg-gray-100 text-gray-600 ring-1 ring-gray-200', dot: 'bg-gray-400' },
};
const getStatus = (s = '') => STATUS_MAP[s.toLowerCase()] || STATUS_MAP.default;

/* ── Derive display status for dispatch rows ── */
const getDispatchDisplayStatus = (client) => {
  if (client.dispatchesProcess?.length > 0) {
    const all = client.dispatchesProcess;
    if (all.every((d) => d.status === 'completed')) return 'completed';
    if (all.some((d) => d.status === 'completed')) return 'partical completed';
    if (all.some((d) => d.status === 'in progress')) return 'in progress';
    return 'upcoming';
  }
  return client.status;
};

/* ══════════════════════════════════════════
   DISPATCH SIDE PANEL  (same design as ProjectTable DispatchPanel)
══════════════════════════════════════════ */
const ViewDispatch = ({ dispatchs, setOpenDispatch, projectId }) => {
  const [qualityModalOpen, setQualityModalOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState('');
  const [selectedProcess, setSelectedProcess] = useState('');
  const [selectedEmp, setSelectedEmp] = useState('');

  const statusIcons = {
    'not yet start': <FaHourglassStart className="text-blue-500" />,
    'in progress': <FaHourglassHalf className="text-amber-500" />,
    completed: <FaCheckCircle className="text-emerald-500" />,
  };

  const statusTransitions = {
    'yet not start': 'inprogress',
    inprogress: 'completed',
    completed: null,
    rejected: 'inprogress',
  };

  const handleDispatchStatus = (dispatchId, processId, employeeId, newStatus) => {
    setSelectedDispatch(dispatchId);
    setSelectedProcess(processId);
    setSelectedEmp(employeeId);
    if (newStatus === 'completed') {
      setReceiptOpen(true);
    } else if (newStatus === 'inprogress') {
      setQualityModalOpen(true);
    } else {
      updateDispatchStatus({ dispatchId, processId, newStatus, employeeId, projectId })
        .then((res) => { toast.success(res.message); setOpenDispatch(false); })
        .catch((err) => toast.error(err?.response?.data?.message));
    }
  };

  const handleQualityCheckSubmit = (progress, description) => {
    const newStatus = progress === 'yes' ? 'inprogress' : 'rejected';
    updateDispatchStatus({ dispatchId: selectedDispatch, processId: selectedProcess, employeeId: selectedEmp, newStatus, projectId, rejectedReason: description })
      .then((res) => { toast.success(res.message); setQualityModalOpen(false); setOpenDispatch(false); })
      .catch((err) => toast.error(err?.response?.data?.message));
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex justify-end"
        style={{ background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(3px)' }}
        onClick={() => setOpenDispatch(false)}
      >
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm h-full bg-white shadow-2xl flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold tracking-widest text-emerald-600 uppercase mb-1">
                Dispatch Records
              </p>
              <h2 className="text-lg font-extrabold text-gray-900">
                {dispatchs?.length} Dispatch{dispatchs?.length !== 1 ? 'es' : ''}
              </h2>
            </div>
            <button
              onClick={() => setOpenDispatch(false)}
              className="w-8 h-8 rounded-xl border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {dispatchs?.length > 0 ? (
              <div className="space-y-3">
                {dispatchs.map((d, i) => {
                  const proc = d.processes?.[0];
                  const nextStatus = statusTransitions[proc?.status];
                  return (
                    <motion.div
                      key={d._id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.06 }}
                      className="border border-gray-100 rounded-2xl p-4 bg-gray-50"
                    >
                      {/* Dispatch card header */}
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-bold text-gray-800 text-sm">Dispatch #{i + 1}</span>
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 capitalize">
                          {d.status} {statusIcons[d.status]}
                        </span>
                      </div>

                      {/* Details grid */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-3">
                        <div><p className="text-gray-400 mb-0.5">Quantity</p><p className="font-semibold text-gray-800">{d.quantity}</p></div>
                        {d.startDate && <div><p className="text-gray-400 mb-0.5">Start</p><p className="font-semibold text-gray-800">{d.startDate.slice(0, 10)}</p></div>}
                        <div><p className="text-gray-400 mb-0.5">Dispatch Date</p><p className="font-semibold text-gray-800">{d.dispatchDate?.slice(0, 10)}</p></div>
                        {proc && (
                          <div>
                            <p className="text-gray-400 mb-0.5">Process Status</p>
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${getStatus(proc.status).pill}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${getStatus(proc.status).dot}`} />
                              {proc.status}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Bill link */}
                      {proc?.status === 'completed' && proc?.bill?.billCopy && (
                        <a
                          href={proc.bill.billCopy}
                          target="_blank"
                          rel="noreferrer"
                          className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-3 py-1 rounded-full transition-colors"
                        >
                          <DocumentCheckIcon className="w-3.5 h-3.5" />
                          View Bill
                        </a>
                      )}

                      {/* Action button */}
                      <div className="border-t border-gray-100 pt-3">
                        <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-2">Update process status</p>
                        {nextStatus ? (
                          <button
                            onClick={() => handleDispatchStatus(d._id, proc?._id, proc?.employee, nextStatus)}
                            className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold bg-gray-900 hover:bg-gray-800 active:scale-95 text-white px-4 py-2 rounded-xl transition-all"
                          >
                            Mark as {nextStatus}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No further actions</span>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-40 text-sm text-gray-400">
                No dispatches available.
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>

      <QualityCheckModal
        isOpen={qualityModalOpen}
        onClose={() => setQualityModalOpen(false)}
        onSubmit={handleQualityCheckSubmit}
      />
      <ReceiptUploadModal
        isOpen={receiptOpen}
        onClose={() => { setReceiptOpen(false); setOpenDispatch(false); }}
        projectId={projectId}
        dispatchInfo={{ dispatchId: selectedDispatch, processId: selectedProcess, employeeId: selectedEmp }}
      />
    </AnimatePresence>
  );
};

/* ══════════════════════════════════════════
   MOBILE CARD
══════════════════════════════════════════ */
const MobileCard = ({ client, index, onStatusChange, onDispatch, statusChanging }) => {
  const displayStatus = getDispatchDisplayStatus(client);
  const sc = getStatus(displayStatus);
  const statusTransitions = {
    'yet not start': 'inprogress',
    inprogress: 'completed',
    completed: null,
    rejected: 'inprogress',
  };
  const nextStatus = client.dispatchesProcess?.length > 0 ? null : statusTransitions[client.status];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
    >
      {/* Colored top strip */}
      <div className={`h-1 ${displayStatus === 'completed' ? 'bg-emerald-400' :
        displayStatus === 'inprogress' ? 'bg-amber-400' :
          displayStatus === 'rejected' ? 'bg-red-400' : 'bg-indigo-400'
        }`} />

      <div className="p-4">
        {/* Top */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="text-[10px] font-bold text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                #{index + 1}
              </span>
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${sc.pill}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                {displayStatus}
              </span>
            </div>
            <h3 className="font-extrabold text-gray-900 text-base leading-tight">{client.project?.name}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{client.process?.name}</p>
          </div>
        </div>

        {/* Data grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 bg-gray-50 rounded-xl p-3 mb-3 text-xs">
          <div>
            <p className="text-gray-400 mb-0.5">Vendor</p>
            <p className="font-semibold text-gray-700">{client.vendor?.name || '—'}</p>
          </div>
          <div>
            <p className="text-gray-400 mb-0.5">Quantity</p>
            <p className="font-bold text-gray-900">{client.project?.quantity}</p>
          </div>
          <div>
            <p className="text-gray-400 mb-0.5">Assigned At</p>
            <p className="font-semibold text-gray-700">{client.assignedAt?.slice(0, 10)}</p>
          </div>
          <div>
            <p className="text-gray-400 mb-0.5">Process</p>
            <p className="font-semibold text-gray-700 truncate">{client.process?.name}</p>
          </div>
          {client.latestUpdatedBy && (
            <div className="col-span-2">
              <p className="text-gray-400 mb-0.5">Updated By</p>
              <p className="flex items-center gap-1 text-xs font-semibold text-emerald-700">
                <MdDone className="bg-emerald-500 text-white rounded-full" />
                {client.latestUpdatedBy}
              </p>
            </div>
          )}
          {client.rejectedBy && (
            <div className="col-span-2">
              <p className="text-gray-400 mb-0.5">Rejected By</p>
              <p className="flex items-center gap-1 text-xs font-semibold text-red-600">
                <MdClose className="bg-red-500 text-white rounded-full" />
                {client.rejectedBy}
              </p>
            </div>
          )}
        </div>

        {/* Footer action */}
        {client.dispatchesProcess?.length > 0 ? (
          <button
            onClick={() => onDispatch(client.dispatchesProcess, client.project?._id)}
            className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 rounded-xl py-2.5 transition-colors"
          >
            ↗ Dispatches
          </button>
        ) : nextStatus ? (
          <button
            onClick={() => onStatusChange(client, nextStatus)}
            disabled={statusChanging}
            className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold bg-gray-900 hover:bg-gray-800 active:scale-95 text-white rounded-xl py-2.5 transition-all disabled:opacity-50"
          >
            Mark as {nextStatus}
          </button>
        ) : (
          <div className="text-center text-xs text-gray-400 py-2">No further actions</div>
        )}
      </div>
    </motion.div>
  );
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const EmployeeProject = () => {
  const [activeTab, setActiveTab] = useState('All');
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [statusChanging, setStatusChanging] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQualityModalOpen, setIsQualityModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [dispatchData, setDispatchData] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 8;
  const { user } = useAuth();

  const tabs = ['All', 'Up Coming', 'Yet Not Start', 'InProgress', 'Completed'];

  const statusTransitions = {
    'yet not start': 'inprogress',
    inprogress: 'completed',
    completed: null,
    rejected: 'inprogress',
  };

  useEffect(() => {
    setLoading(true);
    getemployeeTable(user?._id)
      .then((res) => { setClients(res.data); setLoading(false); })
      .catch((err) => { console.log(err); setLoading(false); });
  }, [user, isModalOpen, dispatchOpen]);

  const handleStatusChange = (client, newStatus) => {
    if (newStatus === 'completed') {
      setSelectedClient(client); setIsModalOpen(true);
    } else if (newStatus === 'inprogress') {
      setSelectedClient(client); setIsQualityModalOpen(true);
    } else {
      if (!window.confirm('Are you sure you want to change the status?')) return;
      updateStatus(client, newStatus);
    }
  };

  const updateStatus = (client, newStatus) => {
    setStatusChanging(true);
    UpdateEmployeeProcessStatus(user?._id, { projectId: client.project?._id, status: newStatus })
      .then((res) => {
        toast.success(res.message);
        setClients((prev) =>
          prev.map((c) => c.project?._id === client.project?._id ? { ...client, status: newStatus } : c)
        );
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setStatusChanging(false));
  };

  const handleQualityCheckSubmit = async (isPassed, description = '') => {
    try {
      const status = isPassed === 'yes' ? 'inprogress' : 'rejected';
      const res = await UpdateEmployeeProcessStatus(user?._id, {
        projectId: selectedClient.project?._id,
        status,
        reason: description,
      });
      toast.success(res.message);
      setClients((prev) =>
        prev.map((c) =>
          c.project?._id === selectedClient.project?._id ? { ...selectedClient, status } : c
        )
      );
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsQualityModalOpen(false);
    }
  };

  const handleDispatchToggle = (data, pid) => {
    const updated = data.map((d) => ({
      ...d,
      processes: d.processes?.filter((p) => p.employee === user?._id),
    }));
    setDispatchData(updated);
    setProjectId(pid);
    setDispatchOpen(true);
  };

  /* ── Filter + search ── */
  const afterTab = activeTab === 'All'
    ? clients
    : clients.filter((c) => c.status.toLowerCase() === activeTab.toLowerCase());

  const filtered = searchTerm.trim()
    ? afterTab.filter((c) =>
      c.project?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.process?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.vendor?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : afterTab;

  /* ── Pagination ── */
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const paginationItems = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
    .reduce((acc, p, i, arr) => {
      if (i > 0 && p - arr[i - 1] > 1) acc.push('e-' + p);
      acc.push(p);
      return acc;
    }, []);

  /* ── Tab counts ── */
  const tabCounts = {
    All: clients.length,
    'Up Coming': clients.filter((c) => c.status.toLowerCase() === 'up coming').length,
    'Yet Not Start': clients.filter((c) => c.status.toLowerCase() === 'yet not start').length,
    InProgress: clients.filter((c) => c.status.toLowerCase() === 'inprogress').length,
    Completed: clients.filter((c) => c.status.toLowerCase() === 'completed').length,
  };

  return (
    <>
      <div className="min-h-screen bg-slate-50 p-3 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">

          {/* ── Page header ── */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight leading-none">
                My Projects
              </h1>
              <p className="text-sm text-gray-400 mt-1.5">
                {clients.length} assigned project{clients.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2.5 shadow-sm flex-1 sm:flex-none sm:w-56 focus-within:ring-2 focus-within:ring-gray-900/10 focus-within:border-gray-400 transition-all">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search projects…"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="text-sm text-gray-700 placeholder-gray-400 outline-none bg-transparent w-full"
                />
              </div>
            </div>
          </div>

          {/* ── Main card ── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

            {/* ── Tabs ── */}
            <div className="flex overflow-x-auto border-b border-gray-100 scrollbar-none">
              {tabs.map((tab) => {
                const active = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                    className={`flex items-center gap-2 px-4 sm:px-5 py-4 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${active
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    {tab}
                    <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                      {tabCounts[tab]}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* ── Loading ── */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <ScaleLoader color="#111827" />
              </div>

            ) : filtered.length === 0 ? (
              /* ── Empty ── */
              <div className="flex flex-col items-center justify-center h-64 gap-3">
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
                  <MagnifyingGlassIcon className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-sm text-gray-400 font-medium">No projects found</p>
                <p className="text-xs text-gray-300">Try adjusting your search or filters</p>
              </div>

            ) : (
              <>
                {/* ════════════════════════════
                    DESKTOP TABLE (md+)
                ════════════════════════════ */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-gray-100">
                        {['#', 'Project Name', 'Process', 'Vendor', 'Assigned At', 'Qty', 'Status', 'Actions'].map((h) => (
                          <th
                            key={h}
                            className="px-5 py-3.5 text-left text-[10px] font-bold tracking-widest text-gray-400 uppercase whitespace-nowrap first:pl-5 last:pr-5"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {paginated.map((client, index) => {
                        const displayStatus = getDispatchDisplayStatus(client);
                        const sc = getStatus(displayStatus);
                        const nextStatus = client.dispatchesProcess?.length > 0
                          ? null
                          : statusTransitions[client.status];

                        return (
                          <motion.tr
                            key={index}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: index * 0.04 }}
                            className="hover:bg-slate-50/80 transition-colors group"
                          >
                            {/* # */}
                            <td className="px-5 py-4 w-10">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-gray-100 text-[10px] font-bold text-gray-500">
                                {(currentPage - 1) * rowsPerPage + index + 1}
                              </span>
                            </td>

                            {/* Project */}
                            <td className="px-5 py-4 max-w-[180px]">
                              <p className="font-semibold text-gray-900 truncate text-[13px]">{client.project?.name}</p>
                            </td>

                            {/* Process */}
                            <td className="px-5 py-4">
                              <p className="text-xs font-semibold text-gray-700">{client.process?.name}</p>
                              {client.latestUpdatedBy && (
                                <p className="flex items-center gap-1 mt-0.5 text-[11px] text-emerald-600">
                                  <MdDone className="bg-emerald-500 text-white rounded-full flex-shrink-0" />
                                  {client.latestUpdatedBy}
                                </p>
                              )}
                              {client.rejectedBy && (
                                <p className="flex items-center gap-1 mt-0.5 text-[11px] text-red-500">
                                  <MdClose className="bg-red-500 text-white rounded-full flex-shrink-0" />
                                  {client.rejectedBy}
                                </p>
                              )}
                            </td>

                            {/* Vendor */}
                            <td className="px-5 py-4 text-xs text-gray-500">{client.vendor?.name || '—'}</td>

                            {/* Assigned At */}
                            <td className="px-5 py-4 text-xs text-gray-500 whitespace-nowrap">
                              {client.assignedAt?.slice(0, 10)}
                            </td>

                            {/* Qty */}
                            <td className="px-5 py-4">
                              <p className="font-bold text-gray-900 text-sm">{client.project?.quantity}</p>
                            </td>

                            {/* Status */}
                            <td className="px-5 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${sc.pill}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                                {displayStatus}
                              </span>
                            </td>

                            {/* Actions */}
                            <td className="px-5 py-4 pr-5">
                              <div className="opacity-60 group-hover:opacity-100 transition-opacity">
                                {client.dispatchesProcess?.length > 0 ? (
                                  <button
                                    onClick={() => handleDispatchToggle(client.dispatchesProcess, client.project?._id)}
                                    className="text-[11px] font-bold text-emerald-600 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-xl transition-colors whitespace-nowrap"
                                  >
                                    ↗ Dispatches
                                  </button>
                                ) : nextStatus ? (
                                  <button
                                    onClick={() => handleStatusChange(client, nextStatus)}
                                    disabled={statusChanging}
                                    className="text-[11px] font-bold bg-gray-900 hover:bg-gray-800 active:scale-95 text-white px-3 py-1.5 rounded-xl transition-all disabled:opacity-50 whitespace-nowrap"
                                  >
                                    Mark as {nextStatus}
                                  </button>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">—</span>
                                )}
                              </div>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ════════════════════════════
                    MOBILE CARDS (< md)
                ════════════════════════════ */}
                <div className="md:hidden p-3 space-y-3">
                  {paginated.map((client, index) => (
                    <MobileCard
                      key={index}
                      client={client}
                      index={(currentPage - 1) * rowsPerPage + index}
                      onStatusChange={handleStatusChange}
                      onDispatch={handleDispatchToggle}
                      statusChanging={statusChanging}
                    />
                  ))}
                </div>
              </>
            )}

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-400 hidden sm:block">
                  Page <span className="font-semibold text-gray-700">{currentPage}</span> of{' '}
                  <span className="font-semibold text-gray-700">{totalPages}</span>
                </p>
                <div className="flex items-center gap-1.5 mx-auto sm:mx-0">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-gray-600 transition-colors shadow-sm"
                  >
                    <ChevronLeftIcon className="w-3.5 h-3.5" />
                  </button>

                  {paginationItems.map((item) =>
                    typeof item === 'string' ? (
                      <span key={item} className="text-gray-400 text-xs px-0.5">···</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setCurrentPage(item)}
                        className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all shadow-sm ${currentPage === item
                          ? 'bg-gray-900 text-white border border-gray-900'
                          : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        {item}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-gray-600 transition-colors shadow-sm"
                  >
                    <ChevronRightIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Modals ── */}
      {dispatchOpen && (
        <ViewDispatch
          dispatchs={dispatchData}
          setOpenDispatch={setDispatchOpen}
          projectId={projectId}
        />
      )}
      <ReceiptUploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        projectId={selectedClient?.project?._id}
      />
      <QualityCheckModal
        isOpen={isQualityModalOpen}
        onClose={() => setIsQualityModalOpen(false)}
        onSubmit={handleQualityCheckSubmit}
      />
    </>
  );
};

export default EmployeeProject;